import { useEffect, useRef, useState } from 'preact/hooks';
import type { BoardEntry } from '@/state/board';
import { initials, avatarClass } from '@/types';
import { dueLabel, timeShort } from '@/lib/due';
import { Icon } from '@/components/Icon';

interface Props {
  entry: BoardEntry;
  onFed: (entry: BoardEntry) => void;
  onSnooze: (entry: BoardEntry) => void;
}

/** Feeding-due card: prominent Fed button (primary) + swipe-right-to-fed
 *  (power use) + long-press opens snooze. HANDOFF §4.4 — both behaviors ship. */
export function FeedingCard({ entry, onFed, onSnooze }: Props) {
  const { animal, schedule, nextDue, state } = entry;
  const [dx, setDx] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const touch = useRef<{ x: number; y: number; swiping: boolean } | null>(null);
  const longPress = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(longPress.current), []);

  const fed = () => {
    setLeaving(true);
    setTimeout(() => onFed(entry), 240);
  };

  const onPointerDown = (e: PointerEvent) => {
    touch.current = { x: e.clientX, y: e.clientY, swiping: false };
    longPress.current = setTimeout(() => {
      if (!touch.current?.swiping) onSnooze(entry);
    }, 550);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!touch.current) return;
    const ddx = e.clientX - touch.current.x;
    const ddy = e.clientY - touch.current.y;
    if (!touch.current.swiping && Math.abs(ddx) > 12 && Math.abs(ddx) > Math.abs(ddy)) {
      touch.current.swiping = true;
      clearTimeout(longPress.current);
    }
    if (touch.current.swiping) setDx(Math.max(0, ddx));
    if (Math.abs(ddx) > 8 || Math.abs(ddy) > 8) clearTimeout(longPress.current);
  };
  const onPointerUp = () => {
    clearTimeout(longPress.current);
    if (dx > 120) fed();
    else setDx(0);
    touch.current = null;
  };

  const amountLine = [
    schedule.amount != null ? `${schedule.amount} ${schedule.unit ?? ''}`.trim() : null,
    schedule.food,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      class={`feed-card ${state === 'overdue' ? 'feed-card--overdue' : ''} ${leaving ? 'is-leaving' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div class="feed-card__swipe-bg" style={{ opacity: Math.min(1, dx / 90) }}>
        <Icon name="check" size={20} /> Fed
      </div>
      <div
        class="feed-card__content"
        style={{ transform: dx ? `translateX(${dx}px)` : undefined, background: 'var(--surface)' }}
      >
        <span class={`avatar ${avatarClass(animal.category)}`}>{initials(animal.name)}</span>
        <div class="feed-card__main">
          <div class="feed-card__name">
            <span>
              {animal.name}
              {animal.species && <span class="species"> · {animal.species.toLowerCase()}</span>}
            </span>
            {animal.kind === 'batch' && <span class="feed-card__count">×{animal.count}</span>}
          </div>
          {nextDue && state === 'overdue' ? (
            <div class="feed-card__due">
              <span class="feed-card__due--overdue">
                <Icon name="clock-slash" />
                Due {timeShort(nextDue)} · {dueLabel(nextDue)}
              </span>
            </div>
          ) : (
            <div class="feed-card__due">
              {nextDue ? `Due ${timeShort(nextDue)}` : 'No schedule'}
              {amountLine ? ` · ${amountLine}` : ''}
            </div>
          )}
        </div>
        <div class="feed-card__actions">
          <button
            class="btn btn--sm"
            style={state === 'overdue' ? { background: 'var(--brand-alert)' } : state === 'scheduled' ? undefined : undefined}
            onClick={(e) => {
              e.stopPropagation();
              fed();
            }}
            aria-label={`Mark ${animal.name} fed`}
          >
            Fed
          </button>
        </div>
      </div>
    </div>
  );
}
